import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Star, MapPin, Clock, Phone, Flame, Zap, Sparkles, Utensils } from "lucide-react";
import { MaskReveal, Reveal, Marquee } from "@/components/Motion";
import { ReviewsTabs } from "@/components/Reviews";
import { MaskLogo, CloudBlob, InstagramIcon } from "@/components/Mask";
import { MENU, MANIFESTO, WHY, CONTACT, STORES } from "@/data/site";

const WHY_ICONS = [Flame, Zap, Sparkles, Utensils];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const yMask = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const signature = MENU.filter((m) => m.signature).slice(0, 4);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden pt-32 pb-20 md:pt-40 md:pb-28">
        <motion.div style={{ y: yMask }} className="pointer-events-none absolute -right-16 top-24 opacity-90">
          <MaskLogo className="h-64 w-64 md:h-96 md:w-96 animate-float" />
        </motion.div>
        <CloudBlob className="pointer-events-none absolute -left-10 bottom-10 h-40 w-64 opacity-10" color="#2D3290" />

        <div className="relative mx-auto max-w-7xl px-5">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
            className="inline-flex flex-col gap-1 rounded-2xl border border-koreyaah-indigo/20 bg-white/60 px-5 py-3 text-xs font-semibold uppercase tracking-widest text-koreyaah-indigo backdrop-blur">
            <span className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-koreyaah-red animate-pulse" /> Now serving · Sarjapur &amp; Begur</span>
            <span className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-koreyaah-cobalt" /> Coming soon · Prestige Tech Park</span>
          </motion.div>

          <h1 className="mt-6 font-display font-bold uppercase leading-[0.85] tracking-tight text-koreyaah-indigo text-6xl sm:text-7xl md:text-8xl lg:text-[8.5rem]">
            <MaskReveal lines={["Your daily", "dose of"]} />
            <span className="block overflow-hidden">
              <motion.span initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className="block text-koreyaah-red">Seoul</motion.span>
            </span>
          </h1>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
            className="mt-8 max-w-xl text-lg md:text-xl text-koreyaah-indigo/80 leading-relaxed">
            Authentic Korean street food, served with Indian warmth. Freshly made, packed with bold flavours, crispy bites, and comforting bowls you'll keep coming back for.
          </motion.p>

          {/* <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85 }}
            className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/menu" data-testid="hero-cta-menu"
              className="group inline-flex items-center gap-2 rounded-full bg-koreyaah-red px-8 py-4 text-base font-bold uppercase tracking-wide text-white transition-transform duration-200 hover:-translate-y-1 hover:bg-koreyaah-red-deep">
              Explore the Menu
              <ArrowRight size={20} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
            <a href={CONTACT.mapsDirections} target="_blank" rel="noreferrer" data-testid="hero-cta-visit"
              className="inline-flex items-center gap-2 rounded-full border-2 border-koreyaah-indigo px-8 py-4 text-base font-bold uppercase tracking-wide text-koreyaah-indigo transition-colors duration-200 hover:bg-koreyaah-indigo hover:text-white">
              <MapPin size={18} /> Visit Us
            </a>
          </motion.div> */}

          <motion.div style={{ y: yImg }} className="mt-16 grid grid-cols-2 gap-4 md:grid-cols-4">
            {signature.map((m, i) => (
              <motion.div key={m.name} initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1 + i * 0.1 }}
                className="group relative overflow-hidden rounded-[1.75rem]">
                <img src={m.img} alt={m.name} loading="lazy" className="h-48 w-full object-cover md:h-56 transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-koreyaah-indigo/85 via-koreyaah-indigo/25 to-transparent px-4 pb-4 pt-12">
                  <span className="text-xs font-bold uppercase tracking-wide text-white">{m.name}</span>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="border-y-2 border-koreyaah-indigo bg-koreyaah-red py-4 text-koreyaah-cream">
        <Marquee className="font-display text-2xl md:text-3xl font-semibold uppercase tracking-wide"
          items={["Now open in Sarjapur & Begur", "Prestige Tech Park coming soon", "Get your Seoul food fix", "Spice, rice & everything nice"]} />
      </div>

      {/* BRAND STORY PREVIEW */}
      <section className="relative mx-auto max-w-7xl px-5 py-24 md:py-32">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <Reveal className="relative">
            <div className="relative overflow-hidden rounded-[2.5rem] shadow-pop">
              <img src="../../../menu/story.jpg" alt="Korean street food spread" loading="lazy" className="h-[420px] w-full object-cover md:h-[520px]" />
            </div>
            <div className="absolute -bottom-6 -right-4 rounded-[1.5rem] bg-koreyaah-cobalt px-6 py-5 text-koreyaah-cream shadow-lg">
              <p className="flex items-baseline gap-1 font-display text-4xl font-bold leading-none">4.7<Star size={22} fill="currentColor" className="text-koreyaah-red self-center" /></p>
              <p className="mt-1 text-xs uppercase tracking-widest text-koreyaah-cream/70">Google rated</p>
            </div>
          </Reveal>
          <div>
            <Reveal><p className="text-sm font-semibold uppercase tracking-[0.3em] text-koreyaah-red">Our Story</p></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-4 font-display text-4xl md:text-5xl font-semibold uppercase tracking-tight text-koreyaah-indigo leading-[0.95]">
                The heart & soul of Korea, in Bangalore
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-lg leading-relaxed text-koreyaah-indigo/80">
              At Koreyaah, we bring the spirit of Korea's vibrant street food culture to every plate. Inspired by the bustling streets of Seoul, our menu is packed with bold flavours, crispy bites, comforting bowls, and dishes made fresh every day. Rooted in authentic Korean recipes and served with warm Indian hospitality, Koreyaah is where great food brings people together.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <Link to="/about" data-testid="story-cta-about" className="group mt-8 inline-flex items-center gap-2 font-bold uppercase tracking-wide text-koreyaah-red">
                Read our full story <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SIGNATURE MENU */}
      <section className="bg-koreyaah-ghost py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-5">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <Reveal><p className="text-sm font-semibold uppercase tracking-[0.3em] text-koreyaah-red">Signature Dishes</p></Reveal>
              <Reveal delay={0.05}><h2 className="mt-3 font-display text-4xl md:text-6xl font-semibold uppercase tracking-tight text-koreyaah-indigo">Fan favourites</h2></Reveal>
            </div>
            <Reveal delay={0.1}>
              <Link to="/menu" data-testid="signature-cta-menu" className="rounded-full border-2 border-koreyaah-indigo px-6 py-3 text-sm font-bold uppercase tracking-wide text-koreyaah-indigo transition-colors duration-200 hover:bg-koreyaah-indigo hover:text-white">Full Menu</Link>
            </Reveal>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {MENU.filter((m) => m.signature || m.tag).slice(0, 6).map((m, i) => (
              <Reveal key={m.name} delay={i * 0.05}>
                <div data-testid={`home-food-card-${i}`} className="group h-full rounded-[2rem] bg-koreyaah-cream p-5 transition-shadow duration-300 hover:shadow-pop">
                  <div className="relative overflow-hidden rounded-[1.5rem]">
                    <img src={m.img} alt={m.name} loading="lazy" className="h-56 w-full object-cover transition-transform duration-700 group-hover:scale-110 group-hover:rotate-1" />
                    {m.tag && <span className="absolute left-3 top-3 rounded-full bg-koreyaah-red px-3 py-1 text-xs font-bold uppercase tracking-wide text-white">{m.tag}</span>}
                  </div>
                  <h3 className="mt-5 font-display text-2xl font-semibold text-koreyaah-indigo">{m.name}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-koreyaah-indigo/70">{m.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="mx-auto max-w-7xl px-5 py-24 md:py-32">
        <Reveal><p className="text-sm font-semibold uppercase tracking-[0.3em] text-koreyaah-red">Why Koreyaah</p></Reveal>
        <Reveal delay={0.05}><h2 className="mt-3 max-w-3xl font-display text-4xl md:text-6xl font-semibold uppercase tracking-tight text-koreyaah-indigo leading-[0.95]">Good food. Warm vibes. Wow moments.</h2></Reveal>
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {WHY.map((w, i) => {
            const Icon = WHY_ICONS[i];
            return (
              <Reveal key={w.title} delay={i * 0.06}>
                <div className="h-full rounded-[2rem] border-2 border-koreyaah-indigo/10 bg-white p-8 transition-transform duration-300 hover:-translate-y-1">
                  <div className="grid h-14 w-14 place-items-center rounded-2xl bg-koreyaah-red text-white"><Icon size={26} /></div>
                  <h3 className="mt-6 font-display text-xl font-semibold text-koreyaah-indigo">{w.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-koreyaah-indigo/70">{w.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="relative overflow-hidden bg-koreyaah-cobalt py-24 md:py-32 text-koreyaah-cream">
        <img src="/brand/pattern1.svg" alt="" aria-hidden="true" className="pointer-events-none absolute -right-24 -top-16 w-[520px] opacity-[0.06]" />
        <div className="relative mx-auto max-w-7xl px-5">
          <Reveal><p className="text-sm font-semibold uppercase tracking-[0.3em] text-koreyaah-red">The Manifesto</p></Reveal>
          <div className="mt-12 space-y-14">
            {MANIFESTO.map((c, i) => (
              <Reveal key={c.no} delay={i * 0.05}>
                <div className="grid gap-6 border-t border-koreyaah-cream/20 pt-10 md:grid-cols-[auto_1fr] md:gap-16">
                  <span className="font-display text-6xl md:text-8xl font-bold text-koreyaah-red leading-none">{c.no}</span>
                  <div>
                    <h3 className="font-display text-3xl md:text-5xl font-semibold uppercase tracking-tight">{c.title}</h3>
                    <p className="mt-4 max-w-2xl text-lg leading-relaxed text-koreyaah-cream/75">{c.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="mx-auto max-w-7xl px-5 py-24 md:py-32">
        <Reveal><p className="text-sm font-semibold uppercase tracking-[0.3em] text-koreyaah-red">Word on the street</p></Reveal>
        <Reveal delay={0.05}><h2 className="mt-3 font-display text-4xl md:text-6xl font-semibold uppercase tracking-tight text-koreyaah-indigo">Loved in Bangalore</h2></Reveal>
        <Reveal delay={0.1}><ReviewsTabs /></Reveal>
      </section>

      {/* INSTAGRAM / SOCIAL */}
      <section className="bg-koreyaah-ghost py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-5">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <Reveal><p className="text-sm font-semibold uppercase tracking-[0.3em] text-koreyaah-red">On the 'gram</p></Reveal>
              <Reveal delay={0.05}><h2 className="mt-3 font-display text-4xl md:text-6xl font-semibold uppercase tracking-tight text-koreyaah-indigo">@koreyaah</h2></Reveal>
            </div>
            <Reveal delay={0.1}>
              <a href={CONTACT.social.instagram} target="_blank" rel="noreferrer" data-testid="instagram-follow" className="inline-flex items-center gap-2 rounded-full bg-koreyaah-red px-6 py-3 text-sm font-bold uppercase tracking-wide text-white transition-transform duration-200 hover:-translate-y-1"><InstagramIcon size={18} /> Follow</a>
            </Reveal>
          </div>
          <div className="mt-12 grid grid-cols-2 gap-4 md:grid-cols-4">
            {MENU.slice(4, 12).slice(0, 8).map((m, i) => (
              <Reveal key={m.name} delay={(i % 4) * 0.05}>
                <a href={CONTACT.social.instagram} target="_blank" rel="noreferrer" className="group relative block overflow-hidden rounded-[1.5rem]">
                  <img src={m.img} alt={m.name} loading="lazy" className="aspect-square w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <span className="absolute inset-0 grid place-items-center bg-koreyaah-indigo/0 transition-colors duration-300 group-hover:bg-koreyaah-indigo/40"><InstagramIcon size={26} className="text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100" /></span>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* STORE LOCATIONS */}
      <section className="mx-auto max-w-7xl px-5 py-24 md:py-32">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Reveal><p className="text-sm font-semibold uppercase tracking-[0.3em] text-koreyaah-red">Find your K-Spot</p></Reveal>
            <Reveal delay={0.05}><h2 className="mt-3 font-display text-4xl md:text-6xl font-semibold uppercase tracking-tight text-koreyaah-indigo">Our outlets</h2></Reveal>
          </div>
        </div>

        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {STORES.map((s, i) => (
            <Reveal key={s.name} delay={i * 0.07}>
              <div data-testid={`home-store-${i}`} className="flex h-full flex-col overflow-hidden rounded-[2rem] border-2 border-koreyaah-indigo/10 bg-white">
                <div className="overflow-hidden">
                  <iframe title={s.name} src={s.embed} className="h-52 w-full grayscale-[0.15]" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="font-display text-xl font-semibold text-koreyaah-indigo">{s.label}</h3>
                    <span className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide ${s.status === "open" ? "bg-koreyaah-red text-white" : "border border-koreyaah-indigo/30 text-koreyaah-indigo/70"}`}>
                      {s.status === "open" ? "Open" : "Coming Soon"}
                    </span>
                  </div>
                  <div className="mt-4 flex flex-1 flex-col text-sm text-koreyaah-indigo/75">
                    <p className="flex flex-1 gap-2"><MapPin size={16} className="mt-0.5 shrink-0 text-koreyaah-red" /><span>{s.address}</span></p>
                    <div className="mt-4 space-y-2.5 border-t border-koreyaah-indigo/10 pt-4">
                      <p className="flex items-center gap-2"><Clock size={16} className="shrink-0 text-koreyaah-red" /> Open daily · {CONTACT.hours}</p>
                      <a href={`tel:${CONTACT.phoneLink}`} className="flex items-center gap-2 transition-colors duration-200 hover:text-koreyaah-red"><Phone size={16} className="shrink-0 text-koreyaah-red" /> {CONTACT.phone}</a>
                    </div>
                  </div>
                  <a href={s.directions} target="_blank" rel="noreferrer" data-testid={`home-store-directions-${i}`} className="group mt-5 inline-flex items-center gap-2 font-bold uppercase tracking-wide text-sm text-koreyaah-red">
                    Get Directions <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
                  </a>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FRANCHISE CTA */}
      <section className="relative overflow-hidden bg-koreyaah-red py-24 md:py-32 text-koreyaah-cream">
        <CloudBlob className="pointer-events-none absolute -right-10 -top-10 h-56 w-96 opacity-10" color="#FFF8F0" />
        <div className="relative mx-auto max-w-5xl px-5 text-center">
          <Reveal><MaskLogo className="mx-auto h-16 w-16" variant="white" /></Reveal>
          <Reveal delay={0.05}><h2 className="mt-6 font-display text-4xl md:text-6xl lg:text-7xl font-bold uppercase tracking-tight leading-[0.9]">Bring Koreyaah to your city</h2></Reveal>
          <Reveal delay={0.1}><p className="mx-auto mt-6 max-w-2xl text-lg text-koreyaah-cream/85">Join one of India's fastest-growing Korean street-food brands. Proven model, compact footprint, full support.</p></Reveal>
          <Reveal delay={0.15}>
            <Link to="/franchise" data-testid="home-franchise-cta" className="mt-10 inline-flex items-center gap-2 rounded-full bg-koreyaah-cream px-9 py-4 text-base font-bold uppercase tracking-wide text-koreyaah-red transition-transform duration-200 hover:-translate-y-1">Explore Franchise <ArrowRight size={20} /></Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
